import type { ChatDialogResponse } from '@/core/generated/api';
import { getChatDialogPreview } from '@/entities/ChatDialog/model/selectors';
import { ChatChannelIcon } from '@/entities/ChatDialog/ui/ChatChannelIcon';
import { formatMoscowTime } from '@/shared/lib/dateTime';
import classes from './ChatIncomingToast.module.scss';

interface ChatIncomingToastProps {
    dialog: ChatDialogResponse;

    /** Открывает переписку с отправителем */
    onOpen: () => void;

    /** Закрывает уведомление */
    onClose: () => void;
}

/** Показывает отправителя и текст нового входящего сообщения */
export const ChatIncomingToast = ({ dialog, onOpen, onClose }: ChatIncomingToastProps) => {
    const name = dialog.companionName || 'Без имени';
    const count = Math.max(0, dialog.unreadCount ?? 0);
    const timestamp = dialog.lastMessage?.sentAt || dialog.lastMessage?.receivedAt || dialog.lastActivityAt;
    const time = formatMoscowTime(timestamp);

    return (
        <div className={classes.notification}>
            <button
                type="button"
                className={classes.content}
                aria-label={`Открыть диалог: ${name}`}
                onClick={onOpen}
            >
                <span className={classes.avatar}>
                    {dialog.companionAvatarUrl
                        ? <img
                            src={dialog.companionAvatarUrl}
                            alt=""
                        />
                        : <span aria-hidden="true">{name.charAt(0).toUpperCase()}</span>}
                    <span className={classes.channelBadge}>
                        <ChatChannelIcon channel={dialog.channel} />
                    </span>
                </span>
                <span className={classes.body}>
                    <span className={classes.heading}>
                        <strong className={classes.name}>{name}</strong>
                        {time &&
                            <time dateTime={timestamp ?? undefined}>{time}</time>
                        }
                    </span>
                    <span className={classes.preview}>{getChatDialogPreview(dialog.lastMessage)}</span>
                </span>
            </button>
            <div className={classes.footer}>
                <span className={classes.counter}>
                    {count > 0
                        ? `Новых сообщений: ${count}`
                        : 'Новое сообщение'}
                </span>
                <button
                    type="button"
                    className={classes.reply}
                    aria-label={`Ответить: ${name}`}
                    onClick={onOpen}
                >
                    Ответить
                </button>
            </div>
            <button
                type="button"
                className={classes.close}
                aria-label="Закрыть уведомление о сообщении"
                onClick={onClose}
            >
                <i
                    className="pi pi-times"
                    aria-hidden="true"
                />
            </button>
        </div>
    );
};
